/* Hallmark: pre-emit critique P4 H4 E4 S5 R4 V3; component-scope, project tokens.
 * States: loading, error/retry, empty, populated, unknown, clipboard success/error.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Copy, Check, X, RefreshCw, AlertTriangle } from 'lucide-react';
import { api } from '../../utils/api';
import type { EquipmentSlotItem, RackReservation } from '../../utils/rackReservation.logic';
import { computeMiniElevationSlots, computeRackUtilizationMetrics } from '../../utils/rackFlyout.logic';
import { normalizeMountingPosition, checkEquipmentConflict } from '../../utils/halfDepth.logic';
import { isZeroUEquipment, checkReservationWarning } from '../../utils/rackMetrics.logic';
import { normalizeRackStatus, normalizeRackRole, RACK_STATUS_METAS, RACK_ROLE_METAS } from '../../utils/rackLifecycle.logic';

export interface RackDetail {
  id: number;
  address_confirmed?: boolean;
  label?: string;
  row_index: number;
  rack_index: number;
  u_slots?: number;
  type?: string;
  is_facility?: boolean;
  is_blocked?: boolean;
  tenant?: string;
  status?: string;
  role?: string;
  max_power_w?: number | null;
  max_weight_kg?: number | null;
  width_m?: number | null;
  depth_m?: number | null;
}
interface SummaryEquipment extends EquipmentSlotItem {
  power_w?: number | null;
  weight_kg?: number | null;
  face?: 'front' | 'rear';
  is_full_depth?: boolean;
}
export interface RackFlyoutDrawerProps {
  rack: RackDetail | null;
  dcCode: string;
  onClose: () => void;
  temperature?: number | null;
  relatedWork?: Array<{ id: string; title: string; status: string }>;
  workLoading?: boolean;
  workError?: boolean;
}
const control = 'min-h-11 min-w-11 rounded border border-border px-3 inline-flex items-center justify-center gap-2 text-sm hover:bg-bg-hover focus-visible:outline-2 focus-visible:outline-accent active:bg-bg-input disabled:opacity-50 disabled:cursor-wait';

export function RackFlyoutDrawer({ rack, dcCode, onClose, temperature, relatedWork = [], workLoading, workError }: RackFlyoutDrawerProps) {
  const location = useLocation();
  const [face, setFace] = useState<'front' | 'rear'>('front');
  const [copyState, setCopyState] = useState<'idle' | 'success' | 'error'>('idle');
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!rack) return;
    const previous = document.activeElement;
    const frame = requestAnimationFrame(() => closeRef.current?.focus());
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKey);
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, [rack?.id, onClose]);
  useEffect(() => { setFace('front'); setCopyState('idle'); }, [rack?.id]);

  const equipmentQuery = useQuery<SummaryEquipment[]>({
    queryKey: ['rack-flyout-equipment', dcCode, rack?.id],
    queryFn: async () => {
      const { data } = await api.get('/api/datacenters/' + encodeURIComponent(dcCode) + '/racks/' + rack!.id + '/equipment');
      if (!Array.isArray(data) || data.some(item => !item || typeof item !== 'object')) throw new Error('Invalid equipment response');
      return data;
    },
    enabled: !!rack && !!dcCode, retry: false,
  });
  // A viewer reads server reservations, never the editing hook's optimistic local cache.
  const reservationsQuery = useQuery<RackReservation[]>({
    queryKey: ['rack-flyout-reservations', dcCode, rack?.id],
    queryFn: async () => {
      const { data } = await api.get('/api/racks/' + rack!.id + '/reservations');
      if (!Array.isArray(data) || data.some(item => !item || typeof item !== 'object')) throw new Error('Invalid reservation response');
      return data;
    },
    enabled: !!rack && !!dcCode, retry: false,
  });
  const equipment = useMemo(() => (equipmentQuery.data ?? []).map(eq => {
    if (isZeroUEquipment(eq)) return eq;
    return { ...eq, mounting_position: eq.is_full_depth === true ? 'full_depth' : eq.face ?? eq.mounting_position };
  }), [equipmentQuery.data]);
  const reservations = reservationsQuery.data ?? [];
  const facility = rack?.is_facility === true;
  const capacity = !facility && Number.isInteger(rack?.u_slots) && rack!.u_slots! > 0 ? rack!.u_slots! : 0;
  const ready = equipmentQuery.isSuccess && reservationsQuery.isSuccess;
  const spaceKnown = ready && capacity > 0;
  const metrics = useMemo(() => computeRackUtilizationMetrics(capacity, equipment, reservations, rack?.max_power_w, rack?.max_weight_kg),
    [capacity, equipment, reservations, rack?.max_power_w, rack?.max_weight_kg]);
  const visibleEquipment = equipment.filter(eq => {
    const mount = normalizeMountingPosition(eq.mounting_position);
    return mount === face || mount === 'full_depth';
  });
  const visibleReservations = reservations.filter(res => !res.face || res.face === 'both' || res.face === face);
  const slots = computeMiniElevationSlots(capacity, visibleEquipment, visibleReservations);
  const powerKnown = equipmentQuery.isSuccess && equipment.every(eq => eq.power_w != null && Number.isFinite(Number(eq.power_w)) && Number(eq.power_w) >= 0);
  const weightKnown = equipmentQuery.isSuccess && equipment.every(eq => eq.weight_kg != null && Number.isFinite(Number(eq.weight_kg)) && Number(eq.weight_kg) >= 0);
  const warnings = useMemo(() => {
    const result: string[] = [];
    if (rack?.is_blocked) result.push('Стойка заблокирована');
    if (!facility && !capacity) result.push('Высота стойки неизвестна');
    if (metrics.maxPowerW == null) result.push('Лимит мощности неизвестен');
    if (metrics.maxWeightKg == null) result.push('Лимит веса неизвестен');
    if (powerKnown && metrics.powerLoadPct != null && metrics.powerLoadPct > 100) result.push('Превышен лимит мощности');
    if (weightKnown && metrics.weightLoadPct != null && metrics.weightLoadPct > 100) result.push('Превышен лимит веса');
    const horizontal = equipment.filter(eq => !isZeroUEquipment(eq));
    for (let i = 0; i < horizontal.length; i++) {
      const eq = horizontal[i];
      if (eq.u_position == null || !Number.isInteger(Number(eq.u_position))) result.push('Без позиции U: ' + (eq.name || 'Устройство'));
      else if (capacity && (Number(eq.u_position) < 1 || Number(eq.u_position) + Number(eq.u_height || 1) - 1 > capacity)) result.push('За границами стойки: ' + (eq.name || 'Устройство'));
      const reservationWarning = checkReservationWarning(eq, reservations);
      if (reservationWarning) result.push(reservationWarning);
      for (let j = 0; j < i; j++) {
        if (eq.u_position == null || horizontal[j].u_position == null) continue;
        const conflict = checkEquipmentConflict(eq, horizontal[j]);
        if (conflict.reason) result.push(conflict.reason);
      }
    }
    return [...new Set(result)];
  }, [rack?.is_blocked, facility, capacity, metrics, powerKnown, weightKnown, equipment, reservations]);

  if (!rack) return null;
  const unconfirmedPlace = !facility && rack.type !== 'pillar' && rack.type !== 'column' && rack.address_confirmed !== true;
  const title = rack.label?.trim() || (unconfirmedPlace ? 'Метка не задана' : 'Адрес не назначен');
  const status = rack.status ? RACK_STATUS_METAS[normalizeRackStatus(rack.status)].labelRu : 'Неизвестно';
  const role = rack.role || rack.type ? RACK_ROLE_METAS[normalizeRackRole(rack.role, rack.type)].labelRu : 'Неизвестно';
  const unknownSpace = facility ? 'Не применимо' : 'Неизвестно';
  const errors = equipmentQuery.isError || reservationsQuery.isError;
  const loading = equipmentQuery.isPending || reservationsQuery.isPending;
  const metricText = (value: number, known: boolean, unit: string) => known ? value + ' ' + unit : 'Неизвестно';
  return (
    <aside data-testid="rack-flyout-drawer" aria-label={'Стойка ' + title}
      className="absolute inset-y-0 right-0 z-30 w-full lg:w-[420px] min-w-0 bg-bg-panel border-l border-border flex flex-col overflow-hidden text-text-primary">
      <header className="px-4 py-3 flex items-start gap-3 border-b border-border shrink-0">
        <div className="min-w-0 flex-1">
          <p className="text-xs text-text-secondary mb-1">{unconfirmedPlace ? 'Сохранённая метка' : 'Фактическая стойка'} · ID {rack.id}</p>
          <h2 className="text-base font-semibold break-words [overflow-wrap:anywhere]">{title}</h2>
          <p className="text-xs text-text-secondary mt-1">{facility ? 'Инженерный объект' : capacity ? capacity + 'U' : 'Высота неизвестна'}</p>
          {unconfirmedPlace && <div className="mt-2 text-xs text-status-warning [overflow-wrap:anywhere]" role="note">
            <p className="font-semibold flex items-center gap-1"><AlertTriangle size={14} aria-hidden="true" className="shrink-0" /><span>Место не подтверждено</span></p>
            <p className="mt-1">Метка не является действующим адресом стойки.</p>
          </div>}
        </div>
        <button ref={closeRef} data-testid="rack-flyout-close" onClick={onClose} aria-label="Закрыть ящик стойки" title="Закрыть" className={control + ' !px-0 shrink-0'}><X size={18} /></button>
      </header>
      <div className="px-4 py-3 flex gap-2 border-b border-border shrink-0">
        <Link to={'/' + encodeURIComponent(dcCode) + '/racks/' + rack.id}
          state={{ floorReturnTo: location.pathname + location.search }}
          data-testid="rack-flyout-passport-link" className={control + ' flex-1 bg-accent text-white whitespace-nowrap'}>
          <ExternalLink size={16} /><span>Открыть стойку</span>
        </Link>
        <button data-testid="rack-flyout-copy-btn" className={control + ' !px-0 shrink-0'} aria-label="Копировать ссылку" title="Копировать ссылку"
          onClick={async () => {
            try { await navigator.clipboard.writeText(window.location.href); setCopyState('success'); }
            catch { setCopyState('error'); }
          }}>{copyState === 'success' ? <Check size={16} /> : <Copy size={16} />}</button>
      </div>
      <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-6 text-sm">
        {copyState !== 'idle' && <p role="status">{copyState === 'success' ? 'Ссылка скопирована' : 'Не удалось скопировать ссылку'}</p>}
        <section aria-label="Паспорт стойки">
          <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-x-3 gap-y-2">
            <dt className="text-text-secondary">Статус</dt><dd className="break-words">{status}</dd>
            <dt className="text-text-secondary">Назначение</dt><dd className="break-words">{role}</dd>
            <dt className="text-text-secondary">Арендатор</dt><dd className="break-words [overflow-wrap:anywhere]">{rack.tenant || 'Неизвестно'}</dd>
            <dt className="text-text-secondary">Ширина × глубина</dt><dd>{rack.width_m && rack.depth_m ? rack.width_m + ' × ' + rack.depth_m + ' м' : 'Неизвестно'}</dd>
          </dl>
          {(rack.width_m == null || rack.depth_m == null) && <p role="status" className="mt-3 text-xs text-text-secondary">Контур оценочный; физическое размещение не подтверждено.</p>}
        </section>
        <section aria-label="Ёмкость шкафа">
          <h3 className="text-sm font-semibold mb-3">Ёмкость шкафа</h3>
          <dl className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-3 gap-y-2 tabular-nums">
            <dt className="text-text-secondary">Занято</dt><dd>{equipmentQuery.isSuccess && capacity ? metrics.space.occupiedU + ' U' : unknownSpace}</dd>
            <dt className="text-text-secondary">Резерв</dt><dd>{spaceKnown ? metrics.space.reservedU + ' U' : unknownSpace}</dd>
            <dt className="text-text-secondary">Свободно целиком</dt><dd data-testid="rack-available-u" className="font-semibold">{spaceKnown ? metrics.space.availableU + ' U' : unknownSpace}</dd>
          </dl>
          {spaceKnown && <div role="meter" aria-label="Заполнение стойки" aria-valuenow={metrics.space.utilizationPct} aria-valuemin={0} aria-valuemax={100}
            className="mt-3 h-2 flex bg-bg-input overflow-hidden rounded-sm">
            <span className="bg-accent" style={{ width: metrics.space.occupiedPct + '%' }} />
            <span style={{ width: metrics.space.reservedPct + '%', background: 'var(--status-warning)' }} />
          </div>}
        </section>
        <section aria-label="Паспортная нагрузка">
          <h3 className="text-sm font-semibold mb-3">Паспортная нагрузка</h3>
          <dl className="space-y-3 tabular-nums">
            <div className="flex justify-between gap-3"><dt className="text-text-secondary">Мощность</dt><dd data-testid="rack-power-total">{metricText(metrics.totalPowerW, powerKnown, 'W')}</dd></div>
            <dd data-testid="rack-power-rating" className="text-xs text-text-secondary">Лимит: {metrics.maxPowerW == null ? 'Неизвестно' : metrics.maxPowerW + ' W'}{powerKnown && metrics.powerLoadPct != null ? ' · ' + metrics.powerLoadPct + '%' : ''}</dd>
            <div className="flex justify-between gap-3"><dt className="text-text-secondary">Вес</dt><dd>{metricText(metrics.totalWeightKg, weightKnown, 'кг')}</dd></div>
            <dd data-testid="rack-weight-rating" className="text-xs text-text-secondary">Лимит: {metrics.maxWeightKg == null ? 'Неизвестно' : metrics.maxWeightKg + ' кг'}{weightKnown && metrics.weightLoadPct != null ? ' · ' + metrics.weightLoadPct + '%' : ''}</dd>
            <div className="flex justify-between gap-3"><dt className="text-text-secondary">Температура</dt><dd data-testid="rack-flyout-temperature">{temperature != null && Number.isFinite(temperature) ? temperature.toFixed(1) + ' °C' : 'Нет данных'}</dd></div>
          </dl>
        </section>
        {errors && <section role="alert" className="space-y-2">
          {equipmentQuery.isError && <p>Не удалось загрузить оборудование</p>}
          {reservationsQuery.isError && <p>Не удалось загрузить резервы</p>}
          <button type="button" className={control} disabled={equipmentQuery.isFetching || reservationsQuery.isFetching}
            onClick={() => { void equipmentQuery.refetch(); void reservationsQuery.refetch(); }}><RefreshCw size={16} />Повторить загрузку</button>
        </section>}
        {loading && <p role="status">Загрузка оборудования и резервов...</p>}
        {warnings.length > 0 && <section aria-label="Предупреждения">
          <h3 className="text-sm font-semibold flex gap-2 items-center mb-2"><AlertTriangle size={16} />Требует внимания</h3>
          <ul className="list-disc pl-4 space-y-2 text-text-secondary break-words">{warnings.map(warning => <li key={warning}>{warning}</li>)}</ul>
        </section>}
        <section aria-label="Элевация стойки">
          <div className="flex items-center justify-between gap-3 mb-3">
            <h3 className="text-sm font-semibold">Элевация{capacity ? ' · ' + capacity + 'U' : ''}</h3>
            <div role="radiogroup" aria-label="Сторона стойки" className="flex shrink-0">
              {(['front', 'rear'] as const).map(side => <label key={side} className={control + ' relative !px-2 cursor-pointer has-[:checked]:bg-bg-input focus-within:outline focus-within:outline-2 focus-within:outline-accent'}>
                <input type="radio" className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" name={'rack-face-' + rack.id} checked={face === side} onChange={() => setFace(side)} />
                {side === 'front' ? 'Спереди' : 'Сзади'}
              </label>)}
            </div>
          </div>
          <div data-testid="mini-elevation-frame" className="border-x-2 border-border bg-bg-app max-h-80 overflow-y-auto">
            {!spaceKnown ? <p className="p-3 text-text-secondary">{facility ? 'U-элевация неприменима' : loading ? 'Загрузка...' : 'Элевация недоступна: данные неполные'}</p> :
              slots.map(slot => <div key={slot.uEnd} className={'flex items-center gap-3 px-2 border-b border-border ' + (slot.type === 'equipment' ? 'bg-bg-input' : '')}
                style={{ minHeight: slot.type === 'empty' ? 24 : Math.max(44, slot.heightU * 24) }}>
                <span className="w-12 shrink-0 text-xs text-text-secondary font-mono tabular-nums">{slot.uEnd === slot.uStart ? slot.uStart : slot.uEnd + '–' + slot.uStart}</span>
                <span className={'min-w-0 break-words [overflow-wrap:anywhere] text-xs ' + (slot.type === 'empty' ? 'text-text-secondary' : 'text-text-primary')}>
                  {slot.type === 'empty' ? 'Свободно' : slot.type === 'reservation' ? 'Резерв · ' + slot.label : slot.label}
                </span>
              </div>)}
          </div>
          {equipmentQuery.isSuccess && metrics.zeroUEquipment.length > 0 && <div className="mt-3">
            <h4 className="text-xs font-semibold mb-2">Zero-U</h4>
            <ul className="space-y-1 text-xs text-text-secondary break-words">{metrics.zeroUEquipment.map((eq, index) => <li key={eq.id ?? index}>{eq.name || 'Устройство без названия'}</li>)}</ul>
          </div>}
        </section>
        <section aria-label="Связанные работы">
          <h3 className="text-sm font-semibold mb-2">Связанные работы</h3>
          {workError ? <p role="alert" className="text-text-secondary">Не удалось загрузить работы</p> : workLoading ? <p role="status">Загрузка работ...</p> :
            relatedWork.length ? <ul className="space-y-2">{relatedWork.map(wo => <li key={wo.id}>
              <Link className={control + ' justify-start w-full break-words'} to={'/' + encodeURIComponent(dcCode) + '/tasks?wo=' + encodeURIComponent(wo.id)}>{wo.title} · {wo.status}</Link>
            </li>)}</ul> : <p className="text-text-secondary">Связанные наряды не найдены</p>}
        </section>
      </div>
    </aside>
  );
}
